import { Helmet } from "react-helmet-async";
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  orgSchema,
  websiteSchema,
  breadcrumbSchema,
} from "@/lib/seo";

/**
 * <Seo /> — renders per-page meta, canonical, Open Graph, Twitter, and JSON-LD.
 * Every page passes a `page` object from PAGE_SEO plus an optional `schemas` array
 * of additional JSON-LD graphs (Product, FAQPage, HowTo, etc.).
 */
export const Seo = ({ page, schemas = [] }) => {
  const canonical = `${SITE_URL}${page.path}`;
  const image = page.image || DEFAULT_OG_IMAGE;
  const keywords = (page.keywords || []).join(", ");

  // Always attach Organization + WebSite + BreadcrumbList; caller can add more.
  const allSchemas = [
    orgSchema(),
    websiteSchema(),
    breadcrumbSchema(page.breadcrumbs || [{ name: "Home", path: "/" }]),
    ...schemas,
  ];

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary */}
      <title>{page.title}</title>
      <meta name="description" content={page.description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="author" content={SITE_NAME} />
      <meta name="publisher" content={SITE_NAME} />
      <meta name="theme-color" content="#fe4c1c" />
      <meta httpEquiv="content-language" content="en-IN" />
      <meta name="geo.region" content="IN-KA" />
      <meta name="geo.placename" content="Bangalore" />
      <meta name="geo.position" content="12.8676;77.6062" />
      <meta name="ICBM" content="12.8676, 77.6062" />

      {/* Open Graph */}
      <meta property="og:type" content={page.ogType || "website"} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={page.title} />
      <meta property="og:description" content={page.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} — ${page.title}`} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={page.title} />
      <meta name="twitter:description" content={page.description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — ${page.title}`} />

      {/* AEO / LLM crawler hints */}
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      <meta name="application-name" content={SITE_NAME} />

      {/* JSON-LD (each graph as its own script tag for reliability) */}
      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
